/**
 * The dispensing partner's executed Monthly RNG Confirmations.
 *
 * A confirmation states a quantity for **every** month of the contract term, not
 * just the one it was issued for. So filing one is not an act about a single
 * period, and this card sits outside the period selector on purpose: dropping
 * the July document can turn June red.
 *
 * That is the defect it exists for. The July 2026 confirmation carried June as
 * 50,319 MMBtu when the month was 50,189. July's own figure was right, both
 * parties signed it, and it took an internal review a month later to find.
 *
 * ## Only the execution date is typed
 *
 * The month table is parsed server-side — one counterparty, one template, a real
 * text layer, unlike the utility statements. The execution date is asked of a
 * person because it decides which revision is authoritative, and nothing on the
 * document orders them correctly: on the live pair `Rev2` is the superseded copy
 * and `Rev1` the corrected one, and the corrected copy prints the *earlier*
 * confirmation date. The table below shows both dates side by side for exactly
 * that reason.
 */

import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { IconAlertTriangle, IconFileText, IconUpload, IconX } from "@tabler/icons-react";
import { useState } from "react";
import {
  useFileTransactionConfirmation,
  useLcfsConfirmations,
  usePreviewConfirmation,
} from "../../api/hooks";
import type { ConfirmationDisagreement } from "../../api/types";
import { ReasonModal } from "../ReasonModal";

export function ConfirmationCard() {
  const confirmationsQ = useLcfsConfirmations();
  const fileConfirmation = useFileTransactionConfirmation();
  const preview = usePreviewConfirmation();
  const [file, setFile] = useState<File | null>(null);
  // `YYYY-MM-DD` from a native date input — a signature date has no timezone,
  // and a Date here would drag one in on the way to the wire.
  const [executedOn, setExecutedOn] = useState("");
  const [reasonOpen, setReasonOpen] = useState(false);
  const [result, setResult] = useState<ConfirmationDisagreement[] | null>(null);

  const reset = () => {
    setFile(null);
    setExecutedOn("");
    preview.reset();
  };

  return (
    <Paper p="md" radius="md" withBorder data-test="lcfs-confirmations">
      <Stack gap="md">
        <Stack gap={2} maw={720}>
          <Text fw={600} size="sm">
            Transaction confirmations
          </Text>
          <Text size="xs" c="dimmed">
            The executed Monthly RNG Confirmation. It restates every month of the contract term, so
            filing one checks each of those months against this ledger — including months that
            closed long ago.
          </Text>
        </Stack>

        {!file ? (
          <Dropzone
            onDrop={(accepted) => {
              const dropped = accepted[0] ?? null;
              setFile(dropped);
              if (!dropped) return;
              // Read the date off the document rather than asking for it. A
              // best-effort pre-fill: a failure leaves a usable empty field, it
              // does not block filing.
              preview.mutate(
                { file: dropped },
                {
                  onSuccess: (read) => {
                    if (read.executed_on) setExecutedOn(read.executed_on);
                  },
                },
              );
            }}
            accept={{ "application/pdf": [".pdf"] }}
            maxFiles={1}
            maxSize={10 * 1024 * 1024}
            loading={fileConfirmation.isPending || preview.isPending}
          >
            <Group justify="center" gap="sm" mih={64} style={{ pointerEvents: "none" }}>
              <Dropzone.Accept>
                <IconUpload size={20} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={20} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconFileText size={20} />
              </Dropzone.Idle>
              <Text size="sm" c="dimmed">
                Drop the executed confirmation (PDF) — its month table is read for you
              </Text>
            </Group>
          </Dropzone>
        ) : (
          <Group gap="xs" align="flex-end" wrap="wrap">
            <Badge variant="light" radius="sm" maw={320}>
              {file.name}
            </Badge>
            {preview.data ? (
              <Badge variant="light" radius="sm" color="gray">
                {preview.data.tc_ref ?? "confirmation"} · {preview.data.months.length} month
                {preview.data.months.length === 1 ? "" : "s"}
              </Badge>
            ) : null}
            <Tooltip
              label="The signature date. Leave blank to use the document's own completion stamp. Not the confirmation date printed on the face — on the real pair that one runs backwards between revisions."
              multiline
              w={300}
              withArrow
            >
              <TextInput
                size="xs"
                w={210}
                label="Executed on"
                description={
                  preview.isPending
                    ? "reading the document…"
                    : preview.data?.executed_on
                      ? "read from the document"
                      : "this copy prints no date — enter it"
                }
                type="date"
                value={executedOn}
                onChange={(e) => setExecutedOn(e.currentTarget.value)}
              />
            </Tooltip>
            <Button
              size="compact-sm"
              loading={fileConfirmation.isPending}
              onClick={() => setReasonOpen(true)}
            >
              File
            </Button>
            <Button size="compact-sm" variant="subtle" onClick={reset}>
              Choose a different file
            </Button>
          </Group>
        )}

        {result ? <Outcome disagreements={result} /> : null}

        {confirmationsQ.data && confirmationsQ.data.length > 0 ? (
          <FiledTable rows={confirmationsQ.data} />
        ) : null}
      </Stack>

      <ReasonModal
        opened={reasonOpen}
        title="File transaction confirmation"
        description="Its month table will be read and every month it states checked against this ledger."
        confirmLabel="File confirmation"
        busy={fileConfirmation.isPending}
        error={fileConfirmation.error}
        onConfirm={(reason) => {
          if (!file) return;
          fileConfirmation.mutate(
            { file, executedOn: executedOn || undefined, reason },
            {
              onSuccess: (filed) => {
                setReasonOpen(false);
                setResult(filed.disagreements);
                reset();
              },
            },
          );
        }}
        onClose={() => setReasonOpen(false)}
      />
    </Paper>
  );
}

function Outcome({ disagreements }: { disagreements: ConfirmationDisagreement[] }) {
  if (disagreements.length === 0) {
    return (
      <Alert color="teal" variant="light">
        <Text size="sm">Filed. Every month it states matches this ledger.</Text>
      </Alert>
    );
  }
  return (
    <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
      <Stack gap={4}>
        <Text size="sm" fw={600}>
          Filed, and it disagrees with {disagreements.length === 1 ? "a month" : "months"} of this
          ledger.
        </Text>
        {disagreements.map((d) => (
          <Text key={d.period} size="sm">
            {d.period}: states {d.stated_mmbtu} MMBtu, ledger holds {d.our_mmbtu}.
          </Text>
        ))}
        <Text size="xs" c="dimmed">
          Either the confirmation needs re-executing, or those periods were loaded wrong.
        </Text>
      </Stack>
    </Alert>
  );
}

function FiledTable({
  rows,
}: {
  rows: NonNullable<ReturnType<typeof useLcfsConfirmations>["data"]>;
}) {
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed" fw={600}>
        On file
      </Text>
      <Table highlightOnHover verticalSpacing="xs" fz="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Executed</Table.Th>
            {/* Beside the execution date on purpose: the two disagree across
                revisions, and a reader who trusts this column reaches for the
                withdrawn document. */}
            <Table.Th>Confirmation date</Table.Th>
            <Table.Th>Contract</Table.Th>
            <Table.Th>Months</Table.Th>
            <Table.Th>File</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, index) => (
            <Table.Tr key={row.id}>
              <Table.Td>
                <Group gap={6}>
                  {row.executed_on}
                  {/* Newest execution first, so the first row is the one every
                      period is graded against. */}
                  {index === 0 ? (
                    <Badge size="xs" color="teal" variant="light">
                      in force
                    </Badge>
                  ) : (
                    <Badge size="xs" color="gray" variant="light">
                      superseded
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td c="dimmed">{row.confirmation_date ?? "—"}</Table.Td>
              <Table.Td>{row.tc_ref ?? "—"}</Table.Td>
              <Table.Td>{row.months_stated}</Table.Td>
              <Table.Td>{row.original_filename}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
